import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import type { UserDropdownDto } from '@zaoblako/shared/modules/security/dtos/user/user-dropdown.dto';
import type { UserDropdownListFindDto } from '@zaoblako/shared/modules/security/dtos/user/user-dropdown-list-find.dto';
import type { UserListDto } from '@zaoblako/shared/modules/security/dtos/user/user.list.dto';
import { UserSearchCacheService } from '@zaoblako/web/core/services/security/user-search-cache.service';
import { MultiSelect } from 'primeng/multiselect';

import { isNil } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'crm-user-search-multi-select',
  templateUrl: './user-search-multi-select.component.html',
  styleUrls: ['./user-search-multi-select.component.css']
})
export class UserSearchMultiSelectComponent implements OnInit, OnDestroy {

  @ViewChild(MultiSelect) public multiSelect: MultiSelect;

  @Input() public selectedUsers: UserListDto[] = [];
  @Output() public selectedUsersChange = new EventEmitter<UserListDto[]>();

  @Input() public labelInput = 'Пользователь';
  @Input() public placeholder = 'Выберите пользователей';
  /** Если true — label внутри поля (p-float-label), иначе label задаёт родитель. */
  @Input() public showFloatLabel = false;

  public users: UserDropdownDto[] = [];
  public loading = false;
  public loadingMore = false;

  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  private searchTerm = '';
  private offset = 0;
  private totalCount = 0;
  private panelWrapper: HTMLElement | null = null;
  private scrollListener: (() => void) | null = null;
  private desiredSelectedIds: number[] = [];
  private hydrating = false;
  private optionsLoaded = false;

  public constructor(
    private readonly userSearchCache: UserSearchCacheService,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  /**
   * Восстанавливает выбранных пользователей по id.
   *
   * @param ids — id пользователей
   */
  @Input()
  public set setSelectedUserIds(ids: number[] | number | string) {
    const normalized = this.normalizeIds(ids);

    if (!normalized.length) {
      return;
    }

    if (this.isSameIds(normalized, this.desiredSelectedIds) && this.isSameIds(normalized, this.getSelectedIds())) {
      return;
    }

    this.desiredSelectedIds = normalized;
    this.ensureSelectedLoaded(normalized);
  }

  public ngOnInit(): void {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((search) => this.fetchFirstPage(search)),
      takeUntil(this.destroy$),
    ).subscribe((result) => {
      this.applyFirstPage(result);
      this.optionsLoaded = true;
      this.loading = false;
      this.cdr.detectChanges();
    }, () => {
      this.loading = false;
      this.cdr.detectChanges();
    });

    if (this.selectedUsers?.length) {
      this.desiredSelectedIds = this.getSelectedIds();
      this.users = this.mergeUnique(this.selectedUsers as unknown as UserDropdownDto[]);
    }

    this.loading = true;
    this.cdr.detectChanges();
    this.search$.next('');
  }

  public ngOnDestroy(): void {
    this.unbindScrollListener();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Обрабатывает ввод в поле поиска пользователя.
   *
   * @param event — событие фильтрации MultiSelect
   */
  public onFilter(event: { filter: string; }): void {
    this.search$.next(event.filter || '');
  }

  /**
   * Эмитит выбранных пользователей.
   *
   * @param users — выбранные пользователи
   */
  public onSelectionChange(users: UserListDto[]): void {
    this.selectedUsers = users || [];
    this.desiredSelectedIds = this.getSelectedIds();
    this.selectedUsersChange.emit(this.selectedUsers);
  }

  /**
   * Подписывается на scroll панели после открытия.
   * Если данные ещё грузятся — дождётся; если уже в кэше — подставит сразу.
   */
  public onPanelShow(): void {
    if (!this.optionsLoaded && !this.loading) {
      const cached = this.userSearchCache.getLoadedState(this.searchTerm);

      if (cached?.items?.length) {
        this.applyLoadedState(cached);
        this.optionsLoaded = true;
        this.refreshOptionsView();
      }
    }
    setTimeout(() => this.bindScrollListener());
  }

  /**
   * Снимает scroll-слушатель при закрытии.
   */
  public onPanelHide(): void {
    this.unbindScrollListener();
  }

  public toUser(user: unknown): UserListDto {
    return user as UserListDto;
  }

  /**
   * Нормализует id в number[].
   *
   * @param ids — сырые id
   * @returns массив чисел
   */
  private normalizeIds(ids: number[] | number | string): number[] {
    if (isNil(ids) || ids === '') {
      return [];
    }

    const raw = Array.isArray(ids) ? ids : [ids];

    return raw.map(Number).filter((id) => !Number.isNaN(id));
  }

  /**
   * Id текущих выбранных пользователей.
   *
   * @returns массив id
   */
  private getSelectedIds(): number[] {
    return (this.selectedUsers || []).map(({ id }) => Number(id));
  }

  /**
   * Сравнивает два набора id без учёта порядка.
   *
   * @param left — первый набор
   * @param right — второй набор
   * @returns true если наборы совпадают
   */
  private isSameIds(left: number[], right: number[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((id) => right.includes(id));
  }

  /**
   * Гарантирует появление выбранных пользователей в options и в ngModel.
   *
   * @param ids — id для выбора
   */
  private ensureSelectedLoaded(ids: number[]): void {
    const missingIds = ids.filter((id) => !this.users.some((user) => Number(user.id) === id));

    if (!missingIds.length) {
      this.applySelected(ids);
      return;
    }

    if (this.hydrating) {
      return;
    }

    this.hydrating = true;

    this.userSearchCache.findByIds(missingIds).pipe(
      takeUntil(this.destroy$),
    ).subscribe((users) => {
      this.hydrating = false;
      this.users = this.mergeUnique(this.users, users);
      this.applySelected(ids);
      this.refreshOptionsView();
    }, () => this.hydrating = false);
  }

  /**
   * Устанавливает выбранных пользователей по id.
   *
   * @param ids — id пользователей
   */
  private applySelected(ids: number[]): void {
    const selected = this.users
      .filter((user) => ids.includes(Number(user.id))) as unknown as UserListDto[];

    if (!selected.length) {
      return;
    }

    setTimeout(() => {
      this.selectedUsers = [...selected];
      this.selectedUsersChange.emit(this.selectedUsers);
    });
  }

  /**
   * Запрашивает первую страницу пользователей через общий кэш.
   *
   * @param search — строка поиска
   * @returns observable страницы
   */
  private fetchFirstPage(search: string) {
    this.searchTerm = search;
    this.offset = 0;
    this.loading = true;
    this.loadingMore = false;
    this.cdr.detectChanges();

    return this.userSearchCache.findPage(search, 0);
  }

  /**
   * Заполняет options из уже накопленного в кэше состояния поиска.
   *
   * @param cached — накопленное состояние
   */
  private applyLoadedState(cached: { items: UserDropdownDto[]; count: number; nextOffset: number; }): void {
    this.users = this.mergeUnique(
      this.selectedUsers as unknown as UserDropdownDto[],
      cached.items,
    );
    this.totalCount = cached.count || 0;
    this.offset = cached.nextOffset || cached.items.length;

    if (this.desiredSelectedIds.length) {
      this.ensureSelectedLoaded(this.desiredSelectedIds);
    }
  }

  /**
   * Заменяет список пользователей первой страницей и восстанавливает выбор.
   *
   * @param result — ответ API
   */
  private applyFirstPage(result: UserDropdownListFindDto): void {
    const cached = this.userSearchCache.getLoadedState(this.searchTerm);

    if (cached?.items?.length) {
      this.applyLoadedState(cached);
    } else {
      const page = result.items || [];
      this.users = this.mergeUnique(
        this.selectedUsers as unknown as UserDropdownDto[],
        page,
      );
      this.totalCount = result.count || 0;
      this.offset = page.length;
    }

    if (this.desiredSelectedIds.length) {
      this.ensureSelectedLoaded(this.desiredSelectedIds);
    }

    this.refreshOptionsView();
  }

  /**
   * Есть ли ещё записи для подгрузки.
   *
   * @returns true если загружено меньше totalCount
   */
  private get hasMore(): boolean {
    return this.offset < this.totalCount;
  }

  /**
   * Подгружает следующую страницу пользователей через общий кэш.
   */
  private loadMore(): void {
    if (this.loading || this.loadingMore || !this.hasMore) {
      return;
    }

    this.loadingMore = true;
    this.cdr.detectChanges();

    this.userSearchCache.findPage(this.searchTerm, this.offset).pipe(
      takeUntil(this.destroy$),
    ).subscribe(() => {
      const cached = this.userSearchCache.getLoadedState(this.searchTerm);

      if (cached) {
        this.applyLoadedState(cached);
      }

      this.loadingMore = false;
      this.refreshOptionsView();
    }, () => {
      this.loadingMore = false;
      this.cdr.detectChanges();
    });
  }

  /**
   * Объединяет списки пользователей без дублей по id.
   *
   * @param lists — списки пользователей
   * @returns уникальный список
   */
  private mergeUnique(...lists: UserDropdownDto[][]): UserDropdownDto[] {
    const map = new Map<number, UserDropdownDto>();

    lists.forEach((list) => {
      (list || []).forEach((user) => {
        if (!isNil(user?.id)) {
          map.set(Number(user.id), user);
        }
      });
    });

    return Array.from(map.values());
  }

  /**
   * Проталкивает новые options в OnPush MultiSelect и пересчитывает клиентский filter.
   */
  private refreshOptionsView(): void {
    this.cdr.detectChanges();

    if (this.multiSelect) {
      this.multiSelect.activateFilter();
      this.cdr.detectChanges();
    }
  }

  /**
   * Обрабатывает scroll панели: у низа — loadMore.
   */
  private onPanelScroll(): void {
    if (!this.panelWrapper || this.loading || this.loadingMore || !this.hasMore) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = this.panelWrapper;

    if (scrollTop + clientHeight >= scrollHeight - 24) {
      this.loadMore();
    }
  }

  /**
   * Вешает scroll-слушатель на контейнер опций MultiSelect.
   */
  private bindScrollListener(): void {
    this.unbindScrollListener();

    const wrapper = document.querySelector('.p-multiselect-panel .p-multiselect-items-wrapper') as HTMLElement | null;

    if (!wrapper) {
      return;
    }

    this.panelWrapper = wrapper;
    this.scrollListener = () => this.onPanelScroll();
    this.panelWrapper.addEventListener('scroll', this.scrollListener);
  }

  /**
   * Удаляет scroll-слушатель панели.
   */
  private unbindScrollListener(): void {
    if (this.panelWrapper && this.scrollListener) {
      this.panelWrapper.removeEventListener('scroll', this.scrollListener);
    }

    this.panelWrapper = null;
    this.scrollListener = null;
  }
}
